import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import injectSheet from 'react-jss';

import { inactivityTimeoutLogout } from '~/api/auth';
import AuthenticationStore from '~/auth/authenticationStore';
import EcCard from '~/components/Shared/EcCard/EcCard';
import { Button } from '~/eds/src/components/Button/Button';
import { withCurrentUser } from '~/hocs/withCurrentUser';
import { getClientFromUrl } from '~/routing/utils';
import {
  redirectToDomainLogin,
  redirectToLoginEmbed,
} from '~/services/redirects';
import { User } from '~/types/User';

import styles from './AuthRequireWarningModal.styles';

const AuthRequireWarningModal = ({
  currentUser,
  classes,
}: {
  currentUser: User;
  classes: any;
}) => {
  const AS = AuthenticationStore();
  const { pathname } = window.location;
  const intl = useIntl();

  const inactiveTimeoutInSeconds = AS.getInactiveTimeoutInSeconds(
    currentUser?.client_config?.inactive_timeout_in_seconds,
  );

  const [showLogoutPopup, setShowLogoutPopup] = useState(false);

  const clearSession = () =>
    inactivityTimeoutLogout().finally(() => AS.clearAuthentication());
  const resetInactiveTimer = () =>
    AS.resetInactiveTimer(inactiveTimeoutInSeconds);

  const getSubdomainInSession = () => {
    return AS.getSubDomainInSession();
  };
  const checkIsSubdomainMatch = () => {
    const subdomainOnTab = getClientFromUrl();
    const subdomainInSession = getSubdomainInSession();
    return (
      subdomainInSession &&
      subdomainOnTab &&
      subdomainInSession === subdomainOnTab
    );
  };

  const onReloadDifferentSubdomain = () => {
    const subdomainOnTab = getClientFromUrl();
    const subdomainInSession = getSubdomainInSession();

    if (!subdomainInSession || !subdomainOnTab) return;
    const { origin, pathname, search } = window.location;
    const path = pathname.replace(subdomainOnTab, subdomainInSession);

    window.location.href = `${origin}${path}${search}`;
  };

  useEffect(() => {
    resetInactiveTimer();

    document.addEventListener('mousemove', resetInactiveTimer);
    document.addEventListener('keypress', resetInactiveTimer);
    return () => {
      document.removeEventListener('mousemove', resetInactiveTimer);
      document.removeEventListener('keypress', resetInactiveTimer);
    };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const isSessionExpired = AS.isAuthSessionExpired();
      const isInactiveTimeout = AS.isInactiveTimeoutReached();
      const isUserLoggedIn = AS.isLoggedIn();

      if (isSessionExpired || isInactiveTimeout || !isUserLoggedIn) {
        setShowLogoutPopup(true);

        if (isInactiveTimeout && isUserLoggedIn) clearSession();
      } else {
        if (!AS.isEmbed()) {
          // no need to check subdomain for embed sessions controlled by iframes
          setShowLogoutPopup(!checkIsSubdomainMatch());
        }
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  if (pathname.includes('folders-permission-check') || !showLogoutPopup)
    return null;

  // TODO: use modal from eds instead once we have way to resolve
  //       cover issue of page overlays
  return (
    <div className={classes.modalOverlay}>
      <div className={classes.modalWrapper}>
        <EcCard>
          <div className={classes.modalBody}>
            <div className={classes.modalBodyTitle}>
              {intl.formatMessage({
                id: 'AuthRequireWarningModal.signInRequiredTitle',
                defaultMessage: 'Sign in Required',
                description: 'Modal title for sign in required',
              })}
            </div>
            <div className={classes.modalBodyContent}>
              <p>
                {' '}
                {intl.formatMessage({
                  id: 'AuthRequireWarningModal.signInRequiredMessage',
                  defaultMessage:
                    'You were signed out of your account. Press "Reload" to sign in to Evisort again.',
                  description: 'Sign in required message',
                })}
              </p>
              <div className={classes.modalActions}>
                <Button
                  variant="primary"
                  text={intl.formatMessage({
                    id: 'AuthRequireWarningModal.reloadButton',
                    defaultMessage: 'Reload',
                    description: 'Reload button text',
                  })}
                  onClick={() => {
                    if (checkIsSubdomainMatch() && !AS.isEmbed()) {
                      window.location.reload();
                    } else {
                      const subdomainInSession = getSubdomainInSession();
                      if (subdomainInSession) {
                        onReloadDifferentSubdomain();
                      } else {
                        AS.isEmbed()
                          ? redirectToLoginEmbed()
                          : redirectToDomainLogin();
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </EcCard>
      </div>
    </div>
  );
};

export default injectSheet(styles)(withCurrentUser(AuthRequireWarningModal));
